/**
 * P云广告服务
 * 封装广告拉取和拦截逻辑
 * 支持多个广告位（按 spaceId 隔离状态）
 */

import { loadPyunSdk, isPyunSdkLoaded, getPyunInstance } from './pyunSdk'
import {
  pyunAppId,
  pyunSpaceId,
  pyunProviderCode,
  pyunProviderUrl,
  pyunExternal
} from '../../config/pyunConfig'
import { logger } from './pyunLogger'
import * as constant from './constant'

/** P云券包外跳后返回支付页时，用于恢复 isPress / code 的 session 标记 */
export const PYUN_COUPON_REDIRECT_KEYS = {
  PAY_MONEY: 'pyun_coupon_redirect_pay_money',
  ESCAPE_OUT: 'pyun_coupon_redirect_escape_out'
}

const PYUN_COUPON_REDIRECT_EXPIRE_MS = 30 * 60 * 1000

/**
 * 跳转 P云券包页前打标记，便于微信返回（bfcache）时恢复支付按钮
 */
export function markPyunCouponRedirect (storageKey, plateNumber) {
  sessionStorage.setItem(storageKey, JSON.stringify({
    plate_number: plateNumber || '',
    timestamp: Date.now()
  }))
}

function needRefreshPayCode (vm) {
  if (parseFloat(vm.actually_paid) > 0) return true
  const escData = vm.escapeInfo || vm.escData
  if (parseFloat(vm.actually_paid) === 0 && escData && Number(escData.total) > 0) return true
  if (parseFloat(vm.actually_paid) === 0 && vm.discount_coupon_ids_send && vm.discount_coupon_ids_send.length > 0) {
    return true
  }
  return false
}

/**
 * 从 P云券包外跳返回后解锁支付按钮并重新拉取微信 code
 */
export function recoverFromPyunCouponRedirect (storageKey, vm) {
  const raw = sessionStorage.getItem(storageKey)
  if (!raw) return false
  try {
    const data = JSON.parse(raw)
    if (!data.timestamp || Date.now() - data.timestamp > PYUN_COUPON_REDIRECT_EXPIRE_MS) {
      sessionStorage.removeItem(storageKey)
      return false
    }
    sessionStorage.removeItem(storageKey)
    vm.isPress = false
    vm.code = ''
    if (vm.$store && typeof vm.$store.commit === 'function') {
      vm.$store.commit(constant.keyCodeVuex, '')
    }
    if (needRefreshPayCode(vm) && typeof vm.getAllCode === 'function') {
      vm.getAllCode()
    }
    logger.info('[PyunAd] 从P云券包页返回，已恢复支付状态', { storageKey })
    return true
  } catch (err) {
    sessionStorage.removeItem(storageKey)
    logger.error('[PyunAd] 恢复支付状态失败', err)
    return false
  }
}

/**
 * 监听 pageshow / visibilitychange，处理外跳 P云后返回
 */
export function bindPyunCouponRedirectListeners (storageKey, vm) {
  const handler = () => recoverFromPyunCouponRedirect(storageKey, vm)
  const onPageShow = (e) => {
    if (e.persisted || sessionStorage.getItem(storageKey)) {
      handler()
    }
  }
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && sessionStorage.getItem(storageKey)) {
      handler()
    }
  }
  window.addEventListener('pageshow', onPageShow)
  document.addEventListener('visibilitychange', onVisibilityChange)
  return { onPageShow, onVisibilityChange }
}

export function unbindPyunCouponRedirectListeners (handlers) {
  if (!handlers) return
  window.removeEventListener('pageshow', handlers.onPageShow)
  document.removeEventListener('visibilitychange', handlers.onVisibilityChange)
}

// 广告状态（按 spaceId 隔离）
const adStateMap = {}

function getAdState (spaceId) {
  if (!adStateMap[spaceId]) {
    adStateMap[spaceId] = { loaded: false, data: null }
  }
  return adStateMap[spaceId]
}

/**
 * 拉取广告
 * @param {Object} options 配置项
 * @param {string} options.openid 用户openid
 * @param {string} options.plate 车牌号 (至少前两位)
 * @param {number} options.tradeValue 交易金额(分)
 * @param {string} options.el 广告容器节点ID（DOM元素ID）
 * @param {string} options.spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @param {string} options.providerCode 流量主编号（可选）
 * @param {string} options.providerUrl 流量主同步回调地址（可选）
 * @returns {Promise<Object>} 广告数据
 */
export async function pullAdvert (options = {}) {
  try {
    const spaceId = options.spaceId || pyunSpaceId
    const el = options.el || 'pyun-ad-banner'

    logger.info('[PyunAd] ==================== 开始拉取广告 ====================')
    logger.info('[PyunAd] 配置参数:', {
      el: el,
      app_id: pyunAppId,
      space_id: spaceId,
      provider_code: options.providerCode || pyunProviderCode,
      external: pyunExternal,
      provider_url: options.providerUrl || pyunProviderUrl
    })
    logger.info('[PyunAd] 业务参数:', {
      openid: options.openid || '(空)',
      plate: options.plate || '(空)',
      tradeValue: options.tradeValue || 0
    })

    // 加载SDK
    logger.info('[PyunAd] 步骤1: 加载SDK...')
    const sdkLoaded = await loadPyunSdk()
    logger.info('[PyunAd] SDK加载结果:', sdkLoaded)
    logger.info('[PyunAd] SDK加载状态:', isPyunSdkLoaded())

    const pyun = getPyunInstance()
    logger.info('[PyunAd] pyun对象:', pyun ? '存在' : '不存在')

    if (!pyun || typeof pyun.advertingPull !== 'function') {
      logger.error('[PyunAd] SDK未正确加载或advertingPull方法不存在')
      throw new Error('SDK未加载')
    }

    // 返回 Promise 处理回调
    return new Promise((resolve, reject) => {
      const config = {
        el: el,
        app_id: pyunAppId,
        openid: options.openid || '',
        space_id: spaceId,
        displayed: 1,
        provider_code: options.providerCode || pyunProviderCode,
        external: pyunExternal,
        provider_url: options.providerUrl || pyunProviderUrl,
        personas: {
          plate: options.plate || '',
          trade_value: options.tradeValue || 0
        },
        success: (data) => {
          logger.info('[PyunAd] 广告加载成功!', data)
          const state = getAdState(spaceId)
          state.loaded = true
          state.data = data
          resolve(data)
        },
        failure: (cause) => {
          logger.error('[PyunAd] 广告加载失败', cause)
          const state = getAdState(spaceId)
          state.loaded = false
          state.data = null
          reject(cause)
        }
      }

      logger.info('[PyunAd] 步骤2: 调用 advertingPull, spaceId:', spaceId)
      pyun.advertingPull(config)
    })
  } catch (err) {
    logger.error('[PyunAd] 拉取广告异常', err)
    throw err
  }
}

/**
 * 广告拦截 (支付时调用)
 * @param {Function} onComplete 拦截回调
 * @param {boolean} enforce 是否强制执行拦截
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 */
export function interceptAdvert (onComplete, enforce = false, spaceId) {
  const sid = spaceId || pyunSpaceId
  const state = getAdState(sid)
  logger.info('[PyunAd] ==================== 执行广告拦截 ====================')
  logger.info('[PyunAd] 当前广告状态:', { loaded: state.loaded, data: state.data })

  const pyun = getPyunInstance()
  if (!pyun || typeof pyun.advertingIntercept !== 'function') {
    logger.error('[PyunAd] SDK未正确加载或advertingIntercept方法不存在')
    if (typeof onComplete === 'function') {
      onComplete({ action: 'NONE' })
    }
    return
  }

  logger.info('[PyunAd] 调用 advertingIntercept, enforce:', enforce)
  pyun.advertingIntercept((event) => {
    logger.info('[PyunAd] 拦截回调触发:', event)
    if (typeof onComplete === 'function') {
      onComplete(event)
    }
  }, enforce)
}

/**
 * 广告是否已加载
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @returns {boolean}
 */
export function isAdLoaded (spaceId) {
  return getAdState(spaceId || pyunSpaceId).loaded
}

/**
 * 获取广告数据
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @returns {Object|null}
 */
export function getAdData (spaceId) {
  return getAdState(spaceId || pyunSpaceId).data
}

/**
 * 重置广告状态
 * @param {string} spaceId 广告位ID（可选，不传则重置所有）
 */
export function resetAdStatus (spaceId) {
  if (spaceId) {
    const state = getAdState(spaceId)
    state.loaded = false
    state.data = null
  } else {
    // 重置所有
    Object.keys(adStateMap).forEach(key => {
      adStateMap[key].loaded = false
      adStateMap[key].data = null
    })
  }
}
